import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import { Loader, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react'
import { useTVNavigation } from '@/hooks/useTVNavigation'
import {
  DEVICE_UNPAIRED_EVENT,
  DeviceMember,
  HttpError,
  STEP_UP_EVENT,
  clearDeviceCredential,
  deviceApi,
  deviceNameFromUserAgent,
  endSharedSession,
  getDeviceCredential,
  getDeviceName,
  handoffUrl,
  hostKind,
  pairingApi,
  startMemberSession,
  storeDeviceCredential,
  storeDisplayCredential,
  takePairingHandoff,
  takeUnpairedNotice,
} from '@/utils/sharedDevice'

/** Codes that may expire unapproved in a row before the screen waits for a tap. */
const MAX_AUTO_RENEWALS = 4
const POLL_MS = 3000
const MAX_POLL_MS = 30000

const TV_BUTTON =
  'min-h-[64px] px-8 rounded-2xl text-2xl font-semibold transition-colors ' +
  'focus:outline-none focus:ring-4 focus:ring-yellow-300 focus:ring-offset-2 focus:ring-offset-indigo-950'

/**
 * /device: pairs this browser as a shared family device, then shows the
 * avatar picker. Works with a pointer or a TV remote (arrow keys + Enter);
 * nothing is typed on the device.
 */
export const DevicePage: React.FC = () => {
  const [paired, setPaired] = useState<boolean>(() => {
    const handoff = takePairingHandoff()
    if (handoff?.display) {
      // Approved as display-only on another host: become a dashboard.
      storeDisplayCredential(handoff.token, handoff.name, 'device')
      window.location.replace('/kiosk')
      return false
    }
    if (handoff) storeDeviceCredential(handoff.token, handoff.name)
    return !!getDeviceCredential()
  })
  const [notice, setNotice] = useState<string | null>(() => takeUnpairedNotice())

  const onUnpaired = useCallback((message: string) => {
    clearDeviceCredential()
    setNotice(message)
    setPaired(false)
  }, [])

  // The periodic credential check (SharedDeviceController) found the device
  // revoked while this page was showing the picker.
  useEffect(() => {
    const onRevoked = (e: Event) => {
      takeUnpairedNotice()
      onUnpaired((e as CustomEvent<{ message: string }>).detail?.message || '')
    }
    window.addEventListener(DEVICE_UNPAIRED_EVENT, onRevoked)
    return () => window.removeEventListener(DEVICE_UNPAIRED_EVENT, onRevoked)
  }, [onUnpaired])

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-purple-950 to-slate-900 text-white flex flex-col">
      {paired ? (
        <MemberPicker onUnpaired={onUnpaired} />
      ) : (
        <PairingView notice={notice} onPaired={() => { setNotice(null); setPaired(true) }} />
      )}
    </div>
  )
}

// ── Pairing ───────────────────────────────────────────────────────────────

type PairPhase =
  | { kind: 'loading' }
  | { kind: 'pending'; code: string; expiresAt: number }
  | { kind: 'finishing' }
  | { kind: 'waiting' }
  | { kind: 'error'; message: string }

const PairingView: React.FC<{ notice: string | null; onPaired: () => void }> = ({ notice, onPaired }) => {
  const [phase, setPhase] = useState<PairPhase>({ kind: 'loading' })
  const [now, setNow] = useState(() => Date.now())
  const expiredInARow = useRef(0)
  const deviceName = useRef(deviceNameFromUserAgent(navigator.userAgent))
  useTVNavigation({ autoFocus: true })

  const requestCode = useCallback(async () => {
    setPhase({ kind: 'loading' })
    try {
      const resp = await pairingApi.requestCode(deviceName.current)
      const expiresAt = Date.parse(resp.expires_at) || Date.now() + (resp.expires_in || 900) * 1000
      setPhase({ kind: 'pending', code: resp.code, expiresAt })
    } catch (err) {
      const status = err instanceof HttpError ? err.status : 0
      setPhase({
        kind: 'error',
        message:
          status === 429
            ? 'Too many codes were requested from this network. Wait a minute, then show a new code.'
            : 'Could not get a pairing code. Check the internet connection, then try again.',
      })
    }
  }, [])

  useEffect(() => {
    requestCode()
  }, [requestCode])

  const onExpired = useCallback(() => {
    expiredInARow.current += 1
    if (expiredInARow.current >= MAX_AUTO_RENEWALS) {
      setPhase({ kind: 'waiting' })
    } else {
      requestCode()
    }
  }, [requestCode])

  const finish = useCallback(
    (slug: string, token: string, display: boolean, code: string) => {
      setPhase({ kind: 'finishing' })
      const host = hostKind()
      if (host.kind === 'other' || (host.kind === 'family' && host.slug === slug)) {
        if (display) {
          // The parent chose "Display only": open the family dashboard.
          storeDisplayCredential(token, deviceName.current, code)
          window.location.replace('/kiosk')
          return
        }
        storeDeviceCredential(token, deviceName.current)
        onPaired()
      } else {
        // The credential travels once, in the fragment; index.html strips it
        // on arrival. replace() keeps this page out of history.
        window.location.replace(handoffUrl(slug, token, deviceName.current, display))
      }
    },
    [onPaired]
  )

  // Poll for approval, at most every 3 seconds, backing off on 429.
  const pendingCode = phase.kind === 'pending' ? phase.code : null
  const pendingExpiry = phase.kind === 'pending' ? phase.expiresAt : 0
  useEffect(() => {
    if (!pendingCode) return
    let cancelled = false
    let delay = POLL_MS
    let timer: ReturnType<typeof setTimeout>

    const tick = async () => {
      if (cancelled) return
      if (Date.now() >= pendingExpiry) {
        onExpired()
        return
      }
      try {
        const r = await pairingApi.checkCode(pendingCode)
        if (cancelled) return
        delay = POLL_MS
        if (r.status === 'approved' && r.token && r.family_slug) {
          expiredInARow.current = 0
          finish(r.family_slug, r.token, r.scope === 'display', pendingCode)
          return
        }
        if (r.status === 'expired') {
          onExpired()
          return
        }
        if (r.status === 'consumed' || r.status === 'revoked') {
          setPhase({
            kind: 'error',
            message: 'This code was approved but can no longer be used on this screen. Show a new code and approve it again.',
          })
          return
        }
      } catch (err) {
        if (cancelled) return
        if (err instanceof HttpError && err.status === 429) {
          delay = Math.min(delay * 2, MAX_POLL_MS)
        } else if (err instanceof HttpError && err.status === 404) {
          onExpired()
          return
        }
      }
      timer = setTimeout(tick, delay)
    }

    timer = setTimeout(tick, delay)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [pendingCode, pendingExpiry, onExpired, finish])

  useEffect(() => {
    if (phase.kind !== 'pending') return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [phase.kind])

  const approveUrl = pendingCode
    ? `${window.location.origin}/approve?code=${encodeURIComponent(pendingCode)}`
    : ''
  const secondsLeft = phase.kind === 'pending' ? Math.max(0, Math.round((phase.expiresAt - now) / 1000)) : 0

  return (
    <main className="flex-1 flex flex-col items-center justify-center px-4 sm:px-8 py-8 sm:py-10">
      <h1 className="text-3xl sm:text-5xl font-bold mb-3 text-center">Set up this screen</h1>
      <p className="text-lg sm:text-2xl text-white/80 mb-6 sm:mb-10 text-center max-w-4xl">
        A parent approves this screen from their phone. Nothing needs to be typed here.
      </p>

      {notice && (
        <div role="status" className="mb-8 flex items-center gap-3 bg-amber-500/20 border border-amber-300/50 rounded-2xl px-6 py-4 text-xl">
          <AlertCircle className="w-7 h-7 text-amber-300 flex-shrink-0" />
          {notice}
        </div>
      )}

      {phase.kind === 'loading' && (
        <div className="flex items-center gap-4 text-2xl" role="status">
          <Loader className="w-10 h-10 animate-spin" /> Getting a code...
        </div>
      )}

      {phase.kind === 'pending' && (
        <div className="w-full max-w-6xl grid md:grid-cols-[auto_1fr] gap-8 md:gap-12 items-center">
          {/* The QR scales down on phones (a spare phone can be a shared device). */}
          <div className="bg-white p-4 sm:p-6 rounded-3xl mx-auto w-full max-w-[328px]" aria-hidden="true">
            <QRCodeSVG value={approveUrl} size={280} level="M" marginSize={0} style={{ width: '100%', height: 'auto' }} />
          </div>
          <div className="min-w-0 text-center md:text-left">
            <p className="text-lg sm:text-2xl text-white/80 mb-2">Scan the code with a parent's phone, or enter</p>
            <p
              className="font-mono font-bold tracking-[0.1em] sm:tracking-[0.15em] leading-none text-[clamp(2.25rem,11vw,8rem)] mb-4 break-all"
              aria-label={`Pairing code ${pendingCode?.split('').join(' ')}`}
            >
              {pendingCode}
            </p>
            <p className="text-lg sm:text-2xl text-white/80 break-words">
              at <span className="font-semibold text-white">{window.location.host}/approve</span> while signed in as a parent.
            </p>
            <p className="text-base sm:text-xl text-white/60 mt-6 flex items-center justify-center md:justify-start gap-3">
              <Loader className="w-6 h-6 animate-spin" />
              Waiting for approval. Code changes in {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}.
            </p>
          </div>
        </div>
      )}

      {phase.kind === 'finishing' && (
        <div className="flex items-center gap-4 text-3xl" role="status">
          <CheckCircle className="w-12 h-12 text-green-400" /> Approved. Opening HousePoints...
        </div>
      )}

      {(phase.kind === 'waiting' || phase.kind === 'error') && (
        <div className="text-center max-w-3xl">
          {phase.kind === 'error' ? (
            <p className="text-2xl mb-8 flex items-start gap-3 text-left">
              <AlertCircle className="w-8 h-8 text-red-300 flex-shrink-0" />
              {phase.message}
            </p>
          ) : (
            <p className="text-2xl mb-8">The code expired before anyone approved it.</p>
          )}
          <button
            type="button"
            onClick={() => {
              expiredInARow.current = 0
              requestCode()
            }}
            className={`${TV_BUTTON} bg-white text-indigo-950 hover:bg-indigo-100 inline-flex items-center gap-3`}
          >
            <RefreshCw className="w-7 h-7" /> Show a new code
          </button>
        </div>
      )}
    </main>
  )
}

// ── Avatar picker ───────────────────────────────────────────────────────

const MemberPicker: React.FC<{ onUnpaired: (message: string) => void }> = ({ onUnpaired }) => {
  const navigate = useNavigate()
  const [members, setMembers] = useState<DeviceMember[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [starting, setStarting] = useState<string | null>(null)
  useTVNavigation({ autoFocus: true })

  const load = useCallback(async () => {
    setError(null)
    try {
      setMembers(await deviceApi.members())
    } catch (err) {
      if (err instanceof HttpError && (err.status === 401 || err.status === 403)) {
        onUnpaired('This screen is no longer paired with a family. A parent can pair it again below.')
      } else {
        setError('Could not load your family. Check the internet connection, then try again.')
      }
    }
  }, [onUnpaired])

  useEffect(() => {
    // Arriving here always ends the previous person's session.
    endSharedSession()
    load()
  }, [load])

  const choose = async (m: DeviceMember) => {
    if (m.is_parent) {
      // A parent session without approval has no parent rights, so ask for
      // approval here first; the parent dashboard opens once it is given.
      window.dispatchEvent(new CustomEvent(STEP_UP_EVENT, { detail: { source: 'picker', parentId: m.id } }))
      return
    }
    setStarting(m.id)
    setError(null)
    try {
      await startMemberSession(m)
      navigate('/dashboard', { replace: true })
    } catch (err) {
      setStarting(null)
      if (err instanceof HttpError && err.status === 401) {
        onUnpaired('This screen is no longer paired with a family. A parent can pair it again below.')
      } else {
        setError(`Could not open HousePoints for ${m.display_name}. Try again.`)
      }
    }
  }

  return (
    <main className="flex-1 flex flex-col items-center px-4 sm:px-8 py-8 sm:py-12">
      <h1 className="text-3xl sm:text-5xl font-bold mb-2 text-center">Who is using HousePoints?</h1>
      <p className="text-lg sm:text-2xl text-white/70 mb-8 sm:mb-12 text-center">{getDeviceName()}</p>

      {error && (
        <div role="alert" className="mb-8 flex items-center gap-4 bg-red-500/20 border border-red-300/50 rounded-2xl px-6 py-4 text-xl">
          <AlertCircle className="w-7 h-7 text-red-300 flex-shrink-0" />
          {error}
          <button type="button" onClick={load} className={`${TV_BUTTON} bg-white/15 hover:bg-white/25 text-xl`}>
            Try again
          </button>
        </div>
      )}

      {!members && !error && (
        <div className="flex items-center gap-4 text-2xl" role="status">
          <Loader className="w-10 h-10 animate-spin" /> Loading...
        </div>
      )}

      {members && members.length === 0 && (
        <p className="text-2xl text-white/80">No family members are set up yet.</p>
      )}

      {members && members.length > 0 && (
        <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-8 w-full max-w-7xl" role="list">
          {members.map((m) => (
            <li key={m.id}>
              <button
                type="button"
                onClick={() => choose(m)}
                disabled={starting !== null}
                className="w-full flex flex-col items-center gap-3 sm:gap-4 p-3 sm:p-6 rounded-3xl bg-white/5 hover:bg-white/15 transition-colors
                  focus:outline-none focus:ring-4 focus:ring-yellow-300 focus:bg-white/15 disabled:opacity-60"
              >
                <Avatar member={m} />
                <span className="text-xl sm:text-2xl font-semibold text-center break-words min-w-0 max-w-full">{m.display_name}</span>
                {m.is_parent && <span className="text-lg text-white/60 -mt-2">Parent</span>}
                {starting === m.id && <Loader className="w-7 h-7 animate-spin" aria-label="Opening" />}
              </button>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-10 sm:mt-14 text-base sm:text-xl text-white/60 text-center max-w-3xl">
        Parent tools on this screen need approval from a parent's phone.
      </p>
    </main>
  )
}

const Avatar: React.FC<{ member: DeviceMember }> = ({ member }) => {
  const [broken, setBroken] = useState(false)
  if (member.avatar_url && !broken) {
    return (
      <img
        src={member.avatar_url}
        alt=""
        onError={() => setBroken(true)}
        className="w-24 h-24 sm:w-36 sm:h-36 rounded-full object-cover bg-white/10"
      />
    )
  }
  if (member.avatar_emoji) {
    return (
      <span aria-hidden="true" className="w-24 h-24 sm:w-36 sm:h-36 rounded-full bg-white/10 flex items-center justify-center text-5xl sm:text-7xl">
        {member.avatar_emoji}
      </span>
    )
  }
  return (
    <span aria-hidden="true" className="w-24 h-24 sm:w-36 sm:h-36 rounded-full bg-indigo-500/60 flex items-center justify-center text-4xl sm:text-6xl font-bold">
      {(member.display_name || '?').charAt(0).toUpperCase()}
    </span>
  )
}

export default DevicePage
